import { $, $$ } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { toastError } from '../lib/toast.js';
import { findSelectMenu, upgradeSelects } from '../lib/select-menu.js';

/**
 * The Automations page.
 *
 * Each rule is saved on its own request rather than the whole list in one: the
 * API refuses a rule that would feed itself, and a single payload would make
 * one bad rule lose the four beside it — the complaint this repository already
 * answered on the Welcome page.
 */
export function initAutomationsPage(): void {
  const found = $<HTMLFormElement>('[data-automations-form]');
  if (!found) return;

  const form: HTMLFormElement = found;
  const guildId = form.dataset.guildId ?? '';

  wireList(form);

  // Delegated like the removal button: rows added after load share it. The
  // change itself already reaches the save bar through the form.
  $<HTMLElement>('[data-list="rule"]', form)?.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement) || target.dataset.field !== 'triggerKind') return;

    const row = target.closest<HTMLElement>('[data-rule-row]');
    if (row) showTriggerRole(row);
  });

  trackDirtyForm({
    form,
    successMessage: ct('settingsSaved', 'Settings saved.'),
    onSave: async () => {
      for (const row of rows(form)) {
        const payload = read(row, guildId);
        if (!payload.name) continue;

        const id = row.dataset.ruleId;

        try {
          const answer = await (id
            ? api.put<{ automation: { _id: string } }>(
                `/api/automations/${id}?guildId=${guildId}`,
                payload,
              )
            : api.post<{ automation: { _id: string } }>(
                `/api/automations?guildId=${guildId}`,
                payload,
              ));

          // A rule created here gains its identifier, so the next save updates
          // it instead of making a second copy.
          row.dataset.ruleId = answer.automation._id;
        } catch (error) {
          // Named, so a refused rule points at itself rather than leaving the
          // member to guess which of five was wrong.
          toastError(`${payload.name}: ${error instanceof Error ? error.message : String(error)}`);
        }
      }
    },
  });
}

/** The triggers whose event names a role, and so the only ones that can filter on one. */
const ROLE_TRIGGERS: ReadonlySet<string> = new Set(['roleGained', 'roleLost']);

/** One row, as the API expects it. */
function read(row: HTMLElement, guildId: string) {
  const kind = nativeSelect(row, 'triggerKind') || 'memberJoin';

  return {
    guildId,
    name: text(row, 'name'),
    enabled: checked(row, 'enabled'),
    trigger: {
      kind,
      channelId: findSelectMenu(row, 'triggerChannelId')?.single ?? '',
      // Dropped for the other triggers: their events carry no role, so a stale
      // choice left in the hidden field would stop the rule from ever firing.
      roleId: ROLE_TRIGGERS.has(kind) ? (findSelectMenu(row, 'triggerRoleId')?.single ?? '') : '',
      contains: '',
    },
    conditions: [],
    actions: [
      {
        kind: nativeSelect(row, 'actionKind') || 'sendMessage',
        channelId: findSelectMenu(row, 'actionChannelId')?.single ?? '',
        roleId: findSelectMenu(row, 'actionRoleId')?.single ?? '',
        content: text(row, 'actionContent'),
        emoji: '',
        delaySeconds: 0,
        sanction: 'warn',
        durationMs: 0,
      },
    ],
  };
}

/* ------------------------------------------------------------------ the rows */

function wireList(form: HTMLFormElement): void {
  $('[data-add="rule"]', form)?.addEventListener('click', () => {
    addRow(form);
  });

  // Delegated: a row added after load would otherwise need its own binding.
  $<HTMLElement>('[data-list="rule"]', form)?.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest('[data-remove]');
    if (!button) return;

    const row = button.closest<HTMLElement>('[data-rule-row]');
    const id = row?.dataset.ruleId;

    /*
     * A stored rule is deleted at once, not on the next save.
     *
     * Leaving it to the save would mean removing a row and closing the page
     * quietly keeps the rule running — the opposite of what the click said.
     */
    if (id) {
      void api
        .delete(`/api/automations/${id}?guildId=${form.dataset.guildId ?? ''}`)
        .catch(() => toastError(ct('automationDeleteFailed', 'The rule could not be deleted.')));
    }

    row?.remove();
    refreshEmptyState(form);
    form.dispatchEvent(new Event('change', { bubbles: true }));
  });

  refreshEmptyState(form);
}

function addRow(form: HTMLFormElement): void {
  const template = $<HTMLTemplateElement>('[data-template="rule"]');
  const list = $<HTMLElement>('[data-list="rule"]', form);
  if (!template || !list) return;

  const holder = document.createElement('div');
  holder.innerHTML = template.innerHTML;

  const row = holder.querySelector('[data-rule-row]');
  if (!row) return;

  list.append(row);

  // The blueprint's `<select>` tags are inert inside a `<template>`.
  upgradeSelects(document);

  refreshEmptyState(form);
  form.dispatchEvent(new Event('change', { bubbles: true }));
  row.querySelector<HTMLInputElement>('input[data-field="name"]')?.focus();
}

function showTriggerRole(row: HTMLElement): void {
  const holder = row.querySelector<HTMLElement>('[data-trigger-role]');
  if (holder) holder.hidden = !ROLE_TRIGGERS.has(nativeSelect(row, 'triggerKind'));
}

function refreshEmptyState(form: HTMLFormElement): void {
  const note = $<HTMLElement>('[data-empty="rule"]', form);
  const list = $<HTMLElement>('[data-list="rule"]', form);
  if (note) note.hidden = (list?.querySelectorAll('[data-rule-row]').length ?? 0) > 0;
}

function rows(form: HTMLFormElement): HTMLElement[] {
  return $$<HTMLElement>('[data-list="rule"] [data-rule-row]', form);
}

/* ---------------------------------------------------------------- the fields */

function field(row: ParentNode, name: string): Element | null {
  return row.querySelector(`[data-field="${CSS.escape(name)}"]`);
}

function text(row: ParentNode, name: string): string {
  const node = field(row, name);
  return node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement
    ? node.value.trim()
    : '';
}

function checked(row: ParentNode, name: string): boolean {
  const node = field(row, name);
  return node instanceof HTMLInputElement ? node.checked : false;
}

function nativeSelect(row: ParentNode, name: string): string {
  const node = field(row, name);
  return node instanceof HTMLSelectElement ? node.value : '';
}
